import { VERSION_SEPARATOR } from "../../shared/references/contentReferences";
import type { ApiSchema } from "../apiSchema";
import type { InputSlot, Selector, ValueKind } from "./authoring/selectorDecoders";
import type { ModelUse } from "./authoring/stepCatalogueApi";
import type { StepDefinition } from "./authoring/stepDecoders";

/**
 * Process items as the Workflow view reads them (`docs/components/processes.md` P-1, P-7,
 * P-13): a Workflow with its steps' roles and exports, a step's kind and the items its
 * selectors read, its whole definition for the card's ports and settings, the tool a tool
 * step runs, and an artifact with the Workflow that made it.
 *
 * `dmw-workflow/1` has no roles: every step of a `/1` Workflow is part of the main
 * pipeline, as `/1` files are read as `/2` (P-1).
 */

export type StepKind = ApiSchema<"StepDefinition">["kind"];

/** `main` steps form the pipeline; `input` steps prepare one of its inputs (P-13). */
export type WorkflowStepRole = ApiSchema<"WorkflowRole">;

export type WorkflowStepStart = ApiSchema<"StepStart">;

/** An output part of an earlier step, or a sub-workflow's exported name. */
export interface OutputPartReference {
  readonly fromStep: string;
  readonly part: string;
}

export interface WorkflowDefinition {
  /** `VersionRef` `<item id>@<version>`. */
  readonly reference: string;
  readonly name: string;
  readonly description: string;
  readonly steps: readonly WorkflowDefinitionStep[];
  /** Exported name → the step output part it stands for. */
  readonly exports: Readonly<Record<string, OutputPartReference>>;
  /** The step inputs its start form fills, as `<step key>.<input>` (`declared_inputs[].destination`); absent where it declares none. */
  readonly startInputs?: readonly string[];
}

/** A Workflow's step: one pinned step, one pinned sub-workflow, or an abstract step (P-13, ADR-0043 D1). */
export type WorkflowDefinitionStep = StepUse | SubWorkflowUse | AbstractStepUse;

type Wires = Readonly<Record<string, OutputPartReference>>;

export interface StepUse {
  readonly kind: "step";
  readonly key: string;
  readonly role: WorkflowStepRole;
  /** `VersionRef` of the step. */
  readonly step: string;
  readonly start: WorkflowStepStart;
  /** Input slot → the part it is bound to. */
  readonly inputBindings: Wires;
}

/** Runs every step of the sub-workflow in its place; those steps start and bind on their own. */
export interface SubWorkflowUse {
  readonly kind: "workflow";
  readonly key: string;
  readonly role: WorkflowStepRole;
  /** `VersionRef` of the sub-workflow. */
  readonly workflow: string;
  /** The sub-workflow's stored declared inputs, by name, wired from this Workflow's earlier steps (ADR-0043 D3). */
  readonly inputBindings: Wires;
  /** The sub-workflow's abstract steps, by key, and the version that fills each (ADR-0043 D2). */
  readonly definitions: BoundDefinitions;
}

/**
 * A place whose definition the Workflow that nests this one binds (ADR-0043 D1): typed by the
 * classes of definition it accepts, with no definition of its own.
 */
export interface AbstractStepUse {
  readonly kind: "abstract";
  readonly key: string;
  readonly role: WorkflowStepRole;
  readonly start: WorkflowStepStart;
  /** Class IRIs a bound version must be one of. */
  readonly accepts: readonly string[];
  /** An optional one may stay unbound; it is then absent from the run. */
  readonly required: boolean;
  readonly label: string;
  readonly description: string;
  /** Slots of the bound definition this Workflow wires. */
  readonly inputBindings: Wires;
}

/** What fills an abstract step: an exact step or Workflow version, and the slots its binding wires. */
export interface DefinitionBinding {
  readonly definition: string;
  readonly inputBindings: Wires;
}

/** The definitions a use binds to its Workflow's abstract steps, by abstract step key; empty where nothing is bound. */
export type BoundDefinitions = Readonly<Record<string, DefinitionBinding>>;

const NO_BOUND_DEFINITIONS: BoundDefinitions = {};

/** A placement as the view reads it, from the file's content (one mapping for the read item and the editor's draft). */
export function workflowStepView(step: ApiSchema<"WorkflowStep">): WorkflowDefinitionStep {
  if ("step" in step) {
    return { kind: "step", key: step.key, role: step.role, step: step.step, start: step.start, inputBindings: wiresView(step.input_bindings) };
  }
  if ("workflow" in step) {
    const definitions = Object.entries(step.definitions ?? {}).map(([key, bound]) => [key, { definition: bound.definition, inputBindings: wiresView(bound.input_bindings) }] as const);
    return { kind: "workflow", key: step.key, role: step.role, workflow: step.workflow, inputBindings: wiresView(step.input_bindings), definitions: Object.fromEntries(definitions) };
  }
  return { kind: "abstract", key: step.key, role: step.role, start: step.start, accepts: step.accepts, required: step.required, label: step.label, description: step.description, inputBindings: wiresView(step.input_bindings) };
}

function wiresView(wires: Readonly<Record<string, ApiSchema<"InputBinding">>> | null | undefined): Wires {
  return Object.fromEntries(Object.entries(wires ?? {}).map(([slot, wire]) => [slot, { fromStep: wire.from_step, part: wire.part }]));
}

/**
 * A placement as it runs inside the use that nests its Workflow (ADR-0043 D5): an abstract
 * step the use binds is the bound step, or the bound Workflow nested in its place, with the
 * abstract step's own wires. The binding's wires name steps of the Workflow that holds the
 * use, so they stay with the use (the graph draws them there). Every other placement, an
 * unbound abstract step included, stays as it is. `isWorkflow` tells a bound Workflow version
 * from a step's.
 */
export function boundPlacement(step: WorkflowDefinitionStep, bound: BoundDefinitions, isWorkflow: (reference: string) => boolean): WorkflowDefinitionStep {
  const binding = step.kind === "abstract" ? bound[step.key] : undefined;
  if (step.kind !== "abstract" || binding === undefined) {
    return step;
  }
  const { key, role, inputBindings } = step;
  return isWorkflow(binding.definition)
    ? { kind: "workflow", key, role, workflow: binding.definition, inputBindings, definitions: NO_BOUND_DEFINITIONS }
    : { kind: "step", key, role, step: binding.definition, start: step.start, inputBindings };
}

/**
 * A Workflow's placements as they run where `bound` nests it (ADR-0043 D5): an abstract step
 * the use binds is the bound step or Workflow, and an optional one it leaves unbound is absent.
 * `null` where nothing nests the Workflow: its abstract steps stay, drawn by their labels.
 */
export function placementsAt(workflow: WorkflowDefinition, bound: BoundDefinitions | null, catalogue: Pick<ProcessCatalogue, "workflows">): readonly WorkflowDefinitionStep[] {
  return workflow.steps.flatMap((step) => {
    const placed = boundPlacement(step, bound ?? NO_BOUND_DEFINITIONS, (reference) => catalogue.workflows.has(reference));
    return placed.kind === "abstract" && !placed.required && bound !== null ? [] : [placed];
  });
}

/** A placement's name: its version's, an abstract step's label, or `undefined` while its version is not read. */
export function placementName(step: WorkflowDefinitionStep, catalogue: Pick<ProcessCatalogue, "steps" | "workflows">): string | undefined {
  switch (step.kind) {
    case "step":
      return catalogue.steps.get(step.step)?.name;
    case "workflow":
      return catalogue.workflows.get(step.workflow)?.name;
    case "abstract":
      return step.label;
  }
}

/** The name of a built-in tool (`builtin:<name>@<version>`), or `null` for a tool item. */
export function builtInToolName(tool: string): string | null {
  return /^builtin:([^@]+)@/.exec(tool)?.[1] ?? null;
}

/** The pinned item a Workflow step names; `null` for an abstract step, which pins none. */
function referenceOfStep(step: WorkflowDefinitionStep): string | null {
  switch (step.kind) {
    case "step":
      return step.step;
    case "workflow":
      return step.workflow;
    case "abstract":
      return null;
  }
}

/** Every version a placement pins: its own, and those a sub-workflow use binds to abstract steps (ADR-0043 D2). */
export function pinnedReferences(step: WorkflowDefinitionStep): readonly string[] {
  const own = referenceOfStep(step);
  const bound = step.kind === "workflow" ? Object.values(step.definitions).map((binding) => binding.definition) : [];
  return [...(own === null ? [] : [own]), ...bound];
}

export interface StepSummary {
  readonly instructions?: string | null;
  readonly choiceLabels?: Readonly<Record<string, string>> | null;
  /** `VersionRef`. */
  readonly reference: string;
  readonly name: string;
  readonly description: string;
  readonly kind: StepKind;
  /** For tool steps: a tool `VersionRef` or `builtin:<name>@<version>`; otherwise `null`. */
  readonly tool: string | null;
  /** What its selectors read, in definition order. */
  readonly selectorReads: readonly SelectorRead[];
  /** The item its `propose` makes a new version of, or `null` if it proposes no item. */
  readonly proposes: ItemTarget | null;
  /** The whole definition: its input slots, output, response and settings. */
  readonly definition: StepDefinition;
  /** What it takes and gives in a Workflow, as module B serves it with the version; `null` where none was served. */
  readonly ports: StepPorts | null;
}

/** A step version's input slots and output parts (W-6), as module B's `StepPortsBody` serves them. */
export interface StepPorts {
  readonly inputs: Readonly<Record<string, InputSlot>>;
  /** Each part with the value kinds it may hold; `null` while its tool doesn't read. */
  readonly outputs: Readonly<Record<string, readonly ValueKind[]>> | null;
}

/** A tool a tool step runs: what it gives, and whether it uses an LLM or an embedding model. */
export interface ToolSummary {
  /** A tool `VersionRef`, or `builtin:<name>@<version>`. */
  readonly reference: string;
  readonly name: string;
  /** Output name → its value kind. */
  readonly outputs: Readonly<Record<string, ValueKind>>;
  /** `null` where only the tool's interface was read, as for a version added in the editor. */
  readonly modelUse: ModelUse | null;
}

/**
 * How a selector names an item (W-4, W-5): floating by ID, pinned to one version, or
 * floating through the role the project's bindings fill (PG-5).
 */
export type ItemTarget =
  | { readonly by: "item"; readonly item: string }
  | { readonly by: "version"; readonly version: string }
  | { readonly by: "role"; readonly role: string };

/** One key per target, for maps: the item ID, the `VersionRef`, or `role:<name>`. */
export function targetKey(target: ItemTarget): string {
  switch (target.by) {
    case "item":
      return target.item;
    case "version":
      return target.version;
    case "role":
      return `role:${target.role}`;
  }
}

/** One selector a step's placeholder or tool input reads. */
export interface SelectorRead {
  /** The placeholder or tool input it fills. */
  readonly input: string;
  readonly selector: Selector;
  /** The items it names; empty for a selector over the content version as a whole. */
  readonly targets: readonly ItemTarget[];
}

/** An artifact a step reads, and the Workflow version whose run proposed it (P-13). */
export interface ArtifactSummary {
  /** `VersionRef` of the version the view shows. */
  readonly reference: string;
  readonly name: string;
  /** From the origin entry of the change set that brought this version; `null` if made by hand. */
  readonly madeBy: { readonly workflow: string; readonly thread: string | null } | null;
}

/** An item other than an artifact that a selector names, such as a label list. */
export interface NamedItem {
  /** `VersionRef` of the version the view shows. */
  readonly reference: string;
  readonly name: string;
  readonly kind: string;
  /** For a label list: how many labels it has; `null` for other kinds or when not read. */
  readonly labelCount: number | null;
}

/**
 * Everything a Workflow pins, read once: the Workflows (itself, sub-workflows and the
 * producers of its artifacts) and steps by `VersionRef`, the tools its tool steps run, and
 * the items selectors name by `targetKey`. Anything missing is drawn as unresolved, never
 * guessed.
 */
export interface ProcessCatalogue {
  readonly workflows: ReadonlyMap<string, WorkflowDefinition>;
  readonly steps: ReadonlyMap<string, StepSummary>;
  /** By the tool reference a tool step names. */
  readonly tools: ReadonlyMap<string, ToolSummary>;
  readonly artifacts: ReadonlyMap<string, ArtifactSummary>;
  readonly namedItems: ReadonlyMap<string, NamedItem>;
  /**
   * Items of each kind in the project's latest content version, for counts such as "4
   * questions"; `null` when no step needs them or they could not be read.
   */
  readonly itemCounts: ReadonlyMap<string, number> | null;
  /**
   * By artifact item ID: the project's Workflow whose step proposes that artifact, for an
   * artifact whose shown version no Workflow run made (seeded, or edited by hand).
   */
  readonly declaredProducers: ReadonlyMap<string, string>;
  /**
   * The value kind each selector version renders, by `selectorVersionKey`, from the step
   * catalogue; `null` for one that renders only text. Empty when no step reads a selector or
   * the catalogue could not be read.
   */
  readonly selectorValueKinds: ReadonlyMap<string, ValueKind | null>;
  /**
   * The readable version (ADR-0033, such as `1.2.0`) of each item the Workflow reads, by
   * `VersionRef`, where it has one. Empty without a project or when it could not be read.
   */
  readonly versions: ReadonlyMap<string, string>;
  /**
   * The project's bindings artifact (PG-5) a role was resolved through, by `VersionRef`:
   * where a role's item is changed. `null` when no read named a role, or without bindings.
   */
  readonly bindings: string | null;
}

/** The key of a selector version in `selectorValueKinds`: `<kind>@<version>`. */
export function selectorVersionKey(selector: Pick<Selector, "kind" | "version">): string {
  return `${selector.kind}@${String(selector.version)}`;
}

/** The item ID a `VersionRef` names. */
export function itemIdOf(reference: string): string {
  const at = reference.indexOf(VERSION_SEPARATOR);
  return at === -1 ? reference : reference.slice(0, at);
}
